import { provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, Injectable, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Store, StoreModule } from '@ngrx/store';
import * as Sentry from '@sentry/browser';
import { filter, take } from 'rxjs/operators';
import version from '../../shared/constants/version.constant';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { RuntimeConfigService } from './core/runtime-config.service';
import * as fromRoot from './core/store';
import { metaReducers, reducers } from './core/store/index';
import * as userActions from './core/store/users/user.actions';
import { EffectsModule } from './effects/effects.module';
import { LayoutModule } from './layout/layout.module';
import { MaterialModule } from './material/material.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material'
import { httpInterceptorProviders } from './http-interceptors';

@Injectable()
class SentryErrorHandler extends ErrorHandler {
    constructor() {
        super();
    }

    handleError(error) {
        Sentry.captureException(error.originalError || error);
        return super.handleError(error);
    }
}

@Injectable()
export class CustomHammerConfig extends HammerGestureConfig {
    overrides = {
        pinch: { enable: false },
        rotate: { enable: false }
    };
}

const providers: any = [
    provideAppInitializer(() => {
        const runtimeConfig = inject(RuntimeConfigService);
        return runtimeConfig.load().then(() => {
            // Sentry init deferred until runtime config is loaded so envName is available
            if (runtimeConfig.envName !== 'local' || runtimeConfig.sentryDebug) {
                Sentry.init({
                    dsn: 'https://031d2fb8ca444601a256dcf9b2616345@sentry.io/194024',
                    environment: runtimeConfig.envName,
                    release: version
                });
            }
        });
    }),
    provideAppInitializer(() => {
        const store = inject(Store<fromRoot.State>);
        return run(store)();
    }),
    { provide: HAMMER_GESTURE_CONFIG, useClass: CustomHammerConfig },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } }
];

providers.push(httpInterceptorProviders);

@NgModule({
    declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        CoreModule,
        EffectsModule,
        LayoutModule,
        MaterialModule,
        StoreModule.forRoot(reducers, { metaReducers })
    ], providers: [
        ...providers,
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Material,
                options: {
                    darkModeSelector: false || 'none'
                }
            },
            ripple: true
        }),
        provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({
            cookieName: 'AH_CSRF_TOKEN',
            headerName: 'X-CSRF-TOKEN',
        }))]
})
export class AppModule { }

export function run(store: Store<fromRoot.State>) {
    return runBlock;

    function runBlock() {
        store.dispatch(new userActions.GetAction());

        return store.select(fromRoot.getUserState).pipe(
            filter((state) => state.complete),
            take(1))
            .toPromise();
    }
}